import api from './client';
import type {
  OptionChain,
  StrategyInfo,
  StrategyPreview,
  StrategyLegDetail,
  AutoBankniftyStatus,
  BnToneStatus,
  AllIndicesStatus,
  BnDirectionalStatus,
  BnDirState,
  LmState,
} from './types';

// ── Option Chain ─────────────────────────────────────────────────────────────

export const fetchExpiries = (index = "BANKNIFTY") =>
  api.get<string[]>(`/banknifty/expiries?index=${index}`).then((r) => r.data);

export const fetchOptionChain = (strikes = 20, expiry?: string, index = "BANKNIFTY") =>
  api.get<OptionChain>(`/banknifty/chain?strikes=${strikes}&index=${index}${expiry ? `&expiry=${expiry}` : ""}`).then((r) => r.data);

// Stock options chain — same response shape as the index chain so frontend
// ChainTable consumes it without schema changes.
export const fetchStockExpiries = (symbol: string) =>
  api.get<string[]>(`/stocks/${encodeURIComponent(symbol)}/expiries`).then((r) => r.data);

export const fetchStockOptionChain = (symbol: string, strikes = 10, expiry?: string) =>
  api.get<OptionChain>(
    `/stocks/${encodeURIComponent(symbol)}/chain?strikes=${strikes}${expiry ? `&expiry=${expiry}` : ""}`,
  ).then((r) => r.data);

// Stock cockpit — equity-trading focused snapshot (indicators + today's
// trades + signal_logs) used by the dashboard's stock-row popup.
export interface StockCockpitIndicators {
  rsi: number | null;
  atr: number | null;
  macd: number | null;
  macd_signal: number | null;
  adx: number | null;
  ema8: number | null;
  ema21: number | null;
  vwap: number | null;
  di_plus: number | null;
  di_minus: number | null;
}
export interface StockCockpitTrade {
  order_id: string;
  side: "BUY" | "SELL";
  qty: number;
  price: number;
  strategy: string;
  status: string;
  time: string;
}
export interface StockCockpitSignal {
  strategy: string;
  signal: string;
  rsi: number | null;
  atr: number | null;
  macd: number | null;
  score: number | null;
  sl_price: number | null;
  tp1_price: number | null;
  executed: boolean;
  reason: string;
  time: string;
}
export interface StockCockpit {
  symbol: string;
  spot: number;
  prev_close: number;
  change: number;
  change_pct: number;
  sector: string;
  lot_size: number;
  strike_step: number;
  tier: number;
  indicators: StockCockpitIndicators;
  trades: StockCockpitTrade[];
  closed_pnl: number;
  signals: StockCockpitSignal[];
  as_of: string;
}
export const fetchStockCockpit = (symbol: string) =>
  api.get<StockCockpit>(`/stocks/${encodeURIComponent(symbol)}/cockpit`).then((r) => r.data);

export const fetchStrategies = () =>
  api.get<StrategyInfo[]>("/banknifty/strategies").then((r) => r.data);

export const previewStrategy = (strategyId: string, index = "BANKNIFTY") =>
  api.get<StrategyPreview>(`/banknifty/strategy/${strategyId}/preview?index=${index}`).then((r) => r.data);

// 2026-04-27: batched preview — replaces 6+ parallel /strategy/{id}/preview
// calls with one HTTP. Backend loops the credit strategies internally.
// Response: {previews: {id: StrategyPreview}, errors: {id: string}}
export interface StrategyPreviewsBatch {
  previews: Record<string, StrategyPreview>;
  errors:   Record<string, string>;
}
export const fetchAllStrategyPreviews = (index = "BANKNIFTY") =>
  api.get<StrategyPreviewsBatch>(`/banknifty/strategies/previews?index=${index}`).then((r) => r.data);

export const executeStrategy = (strategyId: string, legs: StrategyLegDetail[]) =>
  api.post("/banknifty/strategy/execute", { strategy_id: strategyId, legs }).then((r) => r.data);

// ── BankNifty Auto-execution ─────────────────────────────────────────────────

export const fetchAutoBankniftyStatus = () =>
  api.get<AutoBankniftyStatus>("/banknifty/auto/status").then((r) => r.data);

export const toggleAutoBanknifty = () =>
  api.post<{ enabled: boolean }>("/banknifty/auto/toggle").then((r) => r.data);

export const stopAllBanknifty = () =>
  api.post<{ closed_groups: string[]; closed_positions: number; message: string }>(
    "/banknifty/auto/stop-all"
  ).then((r) => r.data);

export const closeGroupLeg = (group_id: string, symbol: string) =>
  api.post<{ order_id: string; symbol: string; side: string; qty: number; price: number; message: string }>(
    "/banknifty/auto/close-leg", { group_id, symbol }
  ).then((r) => r.data);

// ── BankNifty Tone ───────────────────────────────────────────────────────────

export const fetchBnTone = () =>
  api.get<BnToneStatus>("/banknifty/tone").then((r) => r.data);

export const fetchAllIndicesStatus = () =>
  api.get<AllIndicesStatus>("/banknifty/indices/status").then((r) => r.data);

// ── BN Directional Short ─────────────────────────────────────────────────────

export const fetchBnDirectionalState = () =>
  api.get<BnDirectionalStatus>("/banknifty/directional/state").then((r) => r.data);

export const overrideBnDirectional = (direction: BnDirState | "CLOSE") =>
  api.post<{ status: string; previous_state: string; new_state?: string; group_id?: string }>(
    "/banknifty/directional/override", { direction }
  ).then((r) => r.data);

// ── BankNifty blocked dates ──────────────────────────────────────────────────

export const fetchBlockedDates = () =>
  api.get<{ blocked_dates: string[] }>("/banknifty/auto/blocked-dates").then((r) => r.data);

export const addBlockedDate = (date: string) =>
  api.post<{ blocked_dates: string[] }>(`/banknifty/auto/blocked-dates/${date}`).then((r) => r.data);

export const removeBlockedDate = (date: string) =>
  api.delete<{ blocked_dates: string[] }>(`/banknifty/auto/blocked-dates/${date}`).then((r) => r.data);

// ── BankNifty Long Momentum Mode ─────────────────────────────────────────────

export const fetchLmState = () =>
  api.get<LmState>("/banknifty/lm/state").then((r) => r.data);
